<template>
  <div class="upload-image-container">
    <div class="text-wrapper">
      <div class="text-1">
        Transfer after completion, submit the following for confirmation.
      </div>
      <div class="text-2">
        Transfer screenshot <span class="asterisk">*</span> : <span class="limit">( Upload up to 2 images )</span>
      </div>
    </div>
    <div class="upload-wrapper">
      <div
        v-if="imageList.length < 2 && queryType !== 'confirmed'"
        @click="startUpload"
        :class="{ failure: queryType === 'repay' }"
        class="upload-image"
      >
        <div
          v-if="loading"
          class="circle_loading"
        />
        <template v-else>
          <img
            v-if="queryType === 'repay'"
            src="@/assets/H5/order/reupload-img.svg"
            alt=""
          >
          <img
            v-else
            src="@/assets/H5/order/upload-img.svg"
            alt=""
          >
          <div class="text">
            Upload Image
          </div>
        </template>
      </div>
      <div class="image-list">
        <div
          v-if="imageList.length === 0 && queryType !== 'confirmed'"
          @click="previewExampleImage"
          class="example-image"
        >
          <img
            class="image"
            src="https://cdn-image.bestdealer.com/app-bestdealer/1688/order/pc-bank-payment-example.jpeg"
            alt=""
          >
          <div class="text">
            Example Image
          </div>
        </div>
        <div
          v-else
          v-for="(url, index) in imageList"
          :key="index"
          class="image"
        >
          <img
            @click="showPreviewImage(index)"
            class="sku-image"
            :src="url"
            alt=""
          >
          <img
            v-if="!queryType"
            @click="deleteImage(index)"
            class="delete-icon"
            src="@/assets/images/close-icon.svg"
            alt=""
          >
        </div>
      </div>
      <input
        ref="inputRef"
        @change="handleInputChange"
        type="file"
        multiple
        accept="image/*"
        style="display: none;"
      >
    </div>
  </div>
  <div
    v-if="showTextarea"
    class="notes-warpper"
    :class="{ focus: focusTextarea }"
  >
    <textarea
      @focus="focusTextarea = true"
      @blur="focusTextarea = false"
      v-model="remark"
      :disabled="queryType === 'confirmed'"
      maxlength="200"
      placeholder="add a few notes to help you later (Maximum 200 characters)."
    />
  </div>
  <div class="button-wrapper">
    <div
      v-if="queryType !== 'confirmed'"
      @click="handleCancel"
      class="button cancel"
    >
      Cancel
    </div>
    <div
      @click="handleConfirm"
      class="button"
      :class="{ disabled: imageList.length === 0 }"
    >
      {{ queryType === 'confirmed' ? 'I Got It' : 'Confirm' }}
    </div>
  </div>

  <PreviewImage
    v-model="previewImageVisible"
    :image-list="previewImageList"
  />
</template>

<script setup>
import { ref, onMounted, computed, onUpdated, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { metaPixelTrack, buriedPointUtil, gaAnalytics } from '@/utils/utils'
import { uploadImage } from '@/utils/upload'
import { fetchData } from '@/utils/fetch'
import { usePlugin } from '@/hooks/usePlugin'
import PreviewImage from './PreviewImage.vue'

const props = defineProps({
  data: {
    type: Object,
    default: () => {}
  }
})

const emits = defineEmits(['paidSuccess'])

const { $toast } = usePlugin()

const route = useRoute()
const router = useRouter()

const inputRef = ref(null)

const loading = ref(false)

const imageList = ref([])

const previewImageList = ref([])

const remark = ref('')

const showTextarea = ref(true)

const previewImageVisible = ref(false)

const focusTextarea = ref(false)

const orderNo = computed(() => route.params.orderNo)

const queryType = computed(() => route.query.type)

watch(() => props.data, value => {
  if (value) {
    if (queryType.value === 'repay') {
      remark.value = value.b2bPaymentOrderVo.bankTransferVO.remark
      imageList.value = value.b2bPaymentOrderVo.bankTransferVO.receiptInfos
    }

    if (queryType.value === 'confirmed') {
      const remark = value.b2bPaymentOrderVo.bankTransferVO.remark
      if (!remark) {
        showTextarea.value = false
      }
    }
  }
})

const startUpload = () => {
  inputRef.value.click()
}

const handleInputChange = async (e) => {
  const maxlength = 2
  const files = e.target.files

  if (!files.length) return

  if (files.length > maxlength) {
    e.target.value = ''
    $toast('Upload up to 2 images', { type: 'warning' })
    return
  }

  if (files.length === 2) {
    imageList.value = []
  }

  loading.value = true

  let count = 0
  for (const file of files) {
    const { fileUrl } = await uploadImage(file)
    imageList.value.push(fileUrl)
    count++

    if (count === files.length) {
      loading.value = false
    }
  }
}

const deleteImage = (index) => {
  imageList.value.splice(index, 1)
}

const handleCancel = () => {
  if (queryType.value && queryType.value === 'repay') {
    buriedPointUtil({ eventId: 'PRP205' })
  } else {
    buriedPointUtil({ eventId: 'PRP202' })
  }
  router.push('/orders')
}

const handleConfirm = async () => {
  if (queryType.value && queryType.value === 'confirmed') {
    buriedPointUtil({ eventId: 'PRP203' })
    router.push('/orders')
    return
  }

  if (!imageList.value.length) return

  if (queryType.value && queryType.value === 'repay') {
    buriedPointUtil({ eventId: 'PRP204' })
  } else {
    buriedPointUtil({ eventId: 'PRP201' })
  }

  try {
    const params = {
      orderNo: orderNo.value,
      paymentMethodCode: 'BANK_TRANSFER_B2B',
      remark: remark.value,
      transactionNo: '',
      receiptInfos: imageList.value // 图片地址
    }
    const { code, msg } = await fetchData('/appb/b2b/order/createOrderPay', {
      method: 'POST',
      body: JSON.stringify(params)
    })
    if (code === 200) {
      gaAnalytics('P_Pay_Success', 'P_Pay_Success')
      if (localStorage.getItem('buy-again')) {
        gaAnalytics('p_buy_again_success', 'p_buy_again_success')
        localStorage.removeItem('buy-again')
      }
      /**
     * Meta Pixel 埋点：银行转账提交支付审核
     */
      metaPixelTrack('POP401', 'Purchase', {
        content_ids: [...new Set(props.data.b2bOrderDetails.map((item) => item.spuId))],
        content_type: 'product_group',
        currency: '$',
        value: 9,
        num_items: props.data.b2bOrderDetails.reduce((total, cur) => {
          total += cur.skuQuantity
          return total
        }, 0)
      })
      emits('paidSuccess')
    } else {
      throw new Error(msg)
    }
  } catch (error) {
    $toast(error.message, { type: 'error' })

    throw error
  }
}

const previewExampleImage = () => {
  previewImageList.value = [
    'https://cdn-image.bestdealer.com/app-bestdealer/1688/order/pc-bank-payment-example.jpeg'
  ]
  previewImageVisible.value = true
}

const showPreviewImage = (index) => {
  previewImageList.value = []
  previewImageList.value.push(imageList.value[index])
  if (imageList.value.length > 1) {
    previewImageList.value.push(imageList.value.filter((_, i) => i !== index))
  }
  previewImageVisible.value = true
}

onMounted(() => {

})

onUpdated(() => {
  if (queryType.value && queryType.value === 'confirmed') {
    const bankTransferVO = props.data.b2bPaymentOrderVo.bankTransferVO
    imageList.value = bankTransferVO.receiptInfos
    remark.value = bankTransferVO.remark

    if (!remark.value) {
      showTextarea.value = false
    }
  }
})
</script>

<style lang='less' scoped>
.upload-image-container {
  display: flex;
  border: 1px solid #ebf1f5;
  border-radius: 8px;
  margin-top: 24px;
  padding: 32px;

  .text-wrapper {
    .text-1 {
      color: #232323;
      font-size: 16px;
      font-family: mon-600;
      line-height: 20px;
    }

    .text-2 {
      color: #232323;
      font-size: 12px;
      font-family: mon-600;
      line-height: 16px;
      margin-top: 8px;

      .asterisk {
        color: #f53131;
      }

      .limit {
        color: #6e717a;
        font-size: 12px;
        font-family: mon-500;
      }
    }
  }

  .upload-wrapper {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    .upload-image {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      width: 136px;
      height: 136px;
      border-radius: 6px;
      background-color: #EFF4FD;
      cursor: pointer;

      &.failure {
        background-color: #fff5f5;
        border: 1px solid #f5c4c4;
        transition: border .3s ease-in-out;

        &:hover {
          border: 1px solid #009de0;
        }

        .text {
          color: #F54550;
        }
      }

      .circle_loading {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 4px solid #1153d6;
        border-top: 4px solid transparent;
        animation: rotate 1s linear infinite;

        @keyframes rotate {
          0% {
            transform: rotate(0deg);
          }

          100% {
            transform: rotate(360deg);
          }
        }
      }
      .text {
        color: #1153d6;
        font-size: 12px;
        font-family: mon-600;
      }
    }

    .image-list {
      position: relative;
      display: flex;
      gap: 16px;

      .example-image {
        position: relative;
        width: 136px;
        height: 136px;
        border-radius: 6px;
        cursor: pointer;
        color: white;
        font-size: 12px;
        font-family: mon-600;
        overflow: hidden;

        &:hover {
          .image {
            transition: transform .3s ease-in-out;
            transform: scale(1.05);
          }
        }

        &::after {
          position: absolute;
          content: '';
          inset: 0;
          background-color: rgba(0, 0, 0, .6);
        }

        .text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100%;
          text-align: center;
          z-index: 9;
        }
      }

      .image {
        position: relative;
        width: 136px;
        height: 136px;
        border-radius: 6px;
        overflow: hidden;
        cursor: pointer;
        &:hover {
          .sku-image {
            transition: transform .3s ease-in-out;
            transform: scale(1.05);
          }
        }

        .sku-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .delete-icon {
          position: absolute;
          top: 4px;
          right: 4px;
          cursor: pointer;
        }
      }
    }
  }
}

.notes-warpper {
  margin-top: 24px;
  padding: 32px;
  height: 160px;
  border: 1px solid #ebf1f5;
  border-radius: 8px;

  &.focus {
    border: 1.5px solid #232323;
  }

  textarea {
    width: 100%;
    height: 100%;
    outline: none;
    border: none;
    resize: none;
    color: #5c5c5c;
    font-size: 12px;
    font-family: mon-500;

    &::placeholder {
      color: #b2b7c2;
    }

    &:disabled {
      background-color: transparent;
    }
  }
}

.button-wrapper {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 32px;

  .button {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    width: 160px;
    color: white;
    font-size: 14px;
    font-family: mon-600;
    background-color: #052784;
    border-radius: 22px;
    cursor: pointer;

    &.disabled {
      opacity: .4;
    }

    &.cancel {
      background-color: white;
      border: 1px solid var(--main-border-color);
      color: #232323;
    }
  }
}
</style>
